import { useAccountInfo } from 'hooks/billing/useAccountInfo';
import { useBillingInfoQuery } from 'network/queries/useBillingInfoQuery';
import { isReversedPendingCheckout } from 'utils/billing/conversion';
import { getHasExistingSubscription } from 'components/Billing/ConversionPopup/utils';

/**
 * True when the account has reversed the paid Move (now free self-serve) but
 * hasn't bought a self-serve plan yet — so we keep nudging it into checkout (the
 * auto-open popup + the home banner). Combines the reversed-self-serve predicate
 * with a "no active subscription" check so the nudge stops the moment they buy.
 */
export const useReversedPendingCheckout = (): boolean => {
  const { data: accountInfo } = useAccountInfo();
  const isReversed = isReversedPendingCheckout(accountInfo);
  // Only the reversed cohort needs billing-info. This hook mounts app-wide (Main
  // layout + home TrialBanner), so an unguarded query would fire GET /billing-info
  // for every user; gate it so non-reversed accounts never fetch. When disabled the
  // data stays `undefined`, which the check below already treats as "not ready".
  const { data: billingInfo } = useBillingInfoQuery({ enabled: isReversed });

  // Wait for billing-info to actually load before nudging. The conversion record
  // stays terminal `reversed` forever, so account-info alone reads "reversed" even
  // after purchase — treating billing-info's loading `undefined` as "no
  // subscription" would auto-open the popup on every refresh until it resolves.
  return (
    isReversed &&
    billingInfo !== undefined &&
    !getHasExistingSubscription(billingInfo.subscriptionState)
  );
};
